import { ChevronDown, GripVertical, MoreVertical, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { Menu, type MenuItem } from '../Menu';
import { Tooltip } from '../Tooltip';
import { useColumnWidths } from '../table/useColumnWidths';
import { Checkbox } from '../inputs/Checkbox';

/** Both gutters are chrome-owned and fixed — callers only thread content cells. */
const GUTTER = 44;

/**
 * The add-many pattern — reference §12.
 *
 * EVERY surface that lets a user add more than one sub-record uses this: line items,
 * contacts, IDs, attachments. Never a stack of bordered "Item 1 / Item 2" cards with their
 * own `+ Add` buttons, and never a labelled pill button above the header — a
 * repeatable-card stack is drift to migrate on sight.
 *
 * The `+` lives in the `#` gutter of the footer row, and is disabled while the current
 * draft row is incomplete so a misclick cannot spawn a second blank row.
 *
 * **IT IS NEVER EMPTY: one blank row is inserted the moment it has none.** Product call,
 * 01 Oct 2026. A grid whose only content is a header and a `+` asks the reader to discover an
 * icon-only button in a gutter before they can type anything, and the empty state it drew said
 * what the table was for rather than letting them fill it in — the dead first step this table
 * exists to remove. With a row already there the reader types into the first cell.
 */
export function InlineAddTable({
  headers,
  colWidths,
  rowCount,
  onAddRow,
  addDisabled = false,
  addDisabledReason = 'Complete the current row before adding another.',
  addMenu,
  resizeKey,
  flexColumn,
  freezeLeadingColumns = 0,
  selectable = false,
  allSelected = false,
  someSelected = false,
  onToggleAll,
  selectDisabled = false,
  footerContent,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
  searchId,
  stretchToBottom = false,
  addButtonId,
  children,
}: {
  /** Mark required columns with `*` in the header text. */
  /**
   * The column headings. A NUMERIC column passes `{ label, align: 'right' }`, because a
   * header pinned left over a right-aligned value reads as two columns rather than one —
   * the rule `<DataTable>`'s own right-aligned headers already follow.
   */
  headers: (string | { label: string; align?: 'left' | 'right' })[];
  colWidths: number[];
  /**
   * How many rows the caller is rendering. **Required, and that is the enforcement** — the rows
   * are the caller's own state, so the shell cannot count them itself and an optional prop is
   * one the ninth caller forgets. Declaring it is what lets the auto-row rule live here instead
   * of being remembered at every call site.
   */
  rowCount: number;
  onAddRow: () => void;
  /** True while the current draft row is incomplete. */
  addDisabled?: boolean;
  /**
   * WHY the `+` is disabled. The component used to assert one cause — "complete the current
   * row" — for a flag any caller sets for any reason, so the moment a table refused to add for
   * some OTHER reason the tooltip stated something that was not true.
   *
   * It defaults to that sentence, so every call site that was relying on it is unchanged.
   */
  addDisabledReason?: string;
  /**
   * TURNS THE `+` INTO A BUTTON GROUP — product call, 01 Oct 2026, in the reader's own words:
   * *add dropdown option on the + button as button group*.
   *
   * The `+` keeps its own job and its own click; the chevron beside it opens these entries,
   * which are ways of filling the table that are not "one more blank row". **Omitting it leaves
   * the gutter exactly as it was**, which is what every other call site gets.
   *
   * It is the joined-control composition `<SearchGroup>`, `<FieldGroup>` and `<SplitButton>`
   * already draw: the wrapper owns the border and the radius and clips, the segments own
   * neither, and a `border-l` divides them — so the two read as one control rather than as two
   * buttons that happen to touch.
   *
   * An entry is an ordinary `MenuItem`, so it carries its own `disabledReason` and its own
   * permission. It is NOT gated on `addDisabled`: that flag means "the draft row is incomplete,
   * do not spawn a second blank", which says nothing about an action that fills rows outright.
   *
   * **THE CHEVRON DISABLES ITSELF when every entry is dead** — see `menuDead` below. A caller
   * states each entry's own reason and the shell decides whether the control is worth opening,
   * so a menu of greyed rows is not a state this table can reach.
   */
  addMenu?: MenuItem[];
  /** Opt into drag-resizable columns persisted under `table.<resizeKey>.colWidths`. */
  resizeKey?: string;
  /** Index of the soft-flex column that absorbs leftover width. */
  flexColumn?: number;
  /** Freeze the `#` gutter plus N leading data columns during horizontal scroll. */
  freezeLeadingColumns?: number;
  selectable?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleAll?: () => void;
  selectDisabled?: boolean;
  /**
   * Bulk-entry affordance beside the `+` — e.g. a multi-select that spawns one row per
   * pick. A checkbox column with no bulk affordance is dead chrome.
   */
  footerContent?: ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchId?: string;
  stretchToBottom?: boolean;
  addButtonId?: string;
  children: ReactNode;
}) {
  /*
    An inline-add table has no stable column identity — its columns are declared by
    position and never reordered — so the persistence key is the position itself. Index
    pages pass real column keys instead, because there the shell does move columns.
  */
  /*
    ONE BLANK ROW, THE MOMENT THERE ARE NONE — the rule above, enforced where it cannot be
    forgotten rather than seeded at eight call sites.

    `addDisabled` IS HONOURED, and that is what makes it safe to run on every render: a caller
    that refuses to add is one this would otherwise ask on a loop, because the count it is
    guarding on would never move off zero.

    A caller still LOADING is harmless for the same reason the guard is: every one of them
    replaces its rows when the fetch lands, so an auto-row inserted into an empty grid is
    discarded by the load rather than left behind as a stray blank.
  */
  const autoAdded = useRef(false);

  useEffect(() => {
    /*
      THE REF IS WHAT MAKES THIS ADD ONCE, and it is not defensive tidying — without it the
      grid opens with TWO rows in development, which is what shipped for one build and what
      looking at the screen caught.

      React 19's StrictMode deliberately runs every effect twice on mount — effect, cleanup,
      effect — and BOTH runs read the same render's `rowCount`, which is still zero because the
      first run's state update has not committed. So the guard above fires twice and adds twice.
      It is the StrictMode trap `<Modal>`'s focus effect already documents, and it is worse
      here: that one moves focus, where this one WRITES STATE, so the artifact is a row the
      reader has to delete rather than a dev-only flicker.
    */
    if (rowCount > 0) {
      autoAdded.current = false;
      return;
    }

    if (addDisabled || autoAdded.current) return;

    autoAdded.current = true;
    onAddRow();
  }, [rowCount, addDisabled, onAddRow]);

  const resizeColumns = colWidths.map((width, index) => ({ key: `col-${index}`, width }));

  const { widths, startResize } = useColumnWidths(resizeKey ?? 'inline-add', resizeColumns);
  const effective = resizeKey ? widths : colWidths;

  /*
    THE NUMBER GUTTER WIDENS FOR THE GROUP, and for nothing else. `GUTTER` is 44px — enough for
    the 36px `+` and no more — so a chevron beside it would be drawn outside its own column on a
    `table-layout: fixed` grid. The sticky offsets below are unaffected: they offset past the
    SELECT gutter, which has not moved.
  */
  const numberGutter = addMenu ? 72 : GUTTER;

  /*
    THE CHEVRON IS DISABLED WHEN NOTHING BEHIND IT APPLIES — product call, 01 Oct 2026, in the
    reader's own words: *disable the dropdown if not applicable anymore*.

    A menu whose every entry is dead still opened, and what the reader met was a panel of greyed
    rows — a control that answers a click by showing them nothing they can do. Disabled, the
    chevron says so before it is pressed and carries the reason in its own tooltip, which is the
    disable-for-state rule the `+` beside it already follows.

    **It is DERIVED here rather than passed in**, because a caller deciding whether its own
    chevron is live is the second copy of a rule this shell exists to hold — and a caller that
    forgot would leave exactly the dead panel this removes.

    **PERMISSION does not count toward it.** `<Menu>` filters a gated entry out after this, so an
    account that cannot see any of them still gets a live chevron over an empty panel — which is
    `<RowActionMenu>`'s own call: hiding it would tell the reader the table has no other ways to
    add, where the truth is that THIS reader has none.
  */
  const menuDead = addMenu !== undefined && addMenu.every((item) => item.disabledReason != null);

  /*
    ONE entry states its OWN reason; several state a generic one. Naming one of three would be
    picking arbitrarily, and concatenating them is a tooltip nobody reads — so the honest sentence
    is about the control rather than about any one row.
  */
  const menuDeadReason =
    addMenu !== undefined && addMenu.length === 1
      ? (addMenu[0].disabledReason ?? null)
      : 'None of these apply right now.';

  const leadingWidth = (selectable ? GUTTER : 0) + numberGutter;

  /*
    THE COLUMNS FILL THE CONTAINER, AND NO SURPLUS IS LEFT AS WHITESPACE — product call,
    29 Sep 2026, in the reader's own words: *by default, fit the columns so that there will be
    no whitespace on the excess column.*

    The surplus goes to ONE column, by ARITHMETIC. That distinction is the whole fix, and both
    of the shapes it replaces are recorded because each was tried and each failed a different
    way:

    - `width: max-content` + `minWidth: 100%` spread the surplus across EVERY column, so
      dragging one narrower widened all the others — the drag fighting back, which reference §4
      says a resize must never do.
    - `width: 100%` on the chosen `<col>` under a table carrying `min-width: max-content` is
      CIRCULAR: the column's width depends on the table's, which depends on the content. Chromium
      resolves it at **999,242px** — measured, twice, three weeks apart. It renders correctly only
      while the blown column is last and its content short, which is why a screenshot never
      showed it.

    So the floor is a CONCRETE PIXEL SUM rather than `max-content`, and the absorbing column asks
    for `calc(100% - <everything else>px)`. Nothing is circular: every other column is a literal
    width, the gutters are chrome constants, and the arithmetic sums to exactly 100% of whatever
    the table is given.

      container WIDER than the sum   the absorber takes the difference — no whitespace
      container NARROWER             the floor holds, the table scrolls, every column is exact
      another column DRAGGED         its pixel width is honoured; the absorber recomputes

    **The absorber DEFAULTS to the last data column**, which is where a trailing blank band
    appears when nothing claims the surplus. A caller naming `flexColumn` overrules that.

    **DRAGGING THE ABSORBER RELEASES IT** to the width the reader chose, and the surplus then
    has nowhere to go — so the blank band comes back. That is the existing contract kept rather
    than a regression: a reader who sizes a column explicitly has said what they want it to be.
  */
  const absorberIndex = Math.min(Math.max(flexColumn ?? headers.length - 1, 0), headers.length - 1);
  const declaredAbsorber = colWidths[absorberIndex] ?? 0;
  const absorberReleased = (effective[absorberIndex] ?? declaredAbsorber) !== declaredAbsorber;
  const absorber = absorberReleased ? -1 : absorberIndex;

  const gutterTotal = (selectable ? GUTTER : 0) + numberGutter + GUTTER;
  const declaredTotal =
    gutterTotal + colWidths.reduce((sum, width, index) => sum + (effective[index] ?? width), 0);

  return (
    <div className={`flex min-h-0 flex-col ${stretchToBottom ? 'h-full' : ''}`}>
      {/* The search strip sits OUTSIDE the horizontal scroller so it never scrolls away. */}
      {onSearchChange ? (
        <div className="relative shrink-0 pb-2">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            id={searchId}
            name={searchId}
            type="search"
            autoComplete="off"
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="min-h-10 w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm text-slate-600 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
      ) : null}

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200">
        <table
          className="h-full border-collapse"
          /*
            THE TABLE IS EXACTLY ITS DECLARED WIDTHS unless a caller asks for a flex column.

            It was `width: 'max-content'` with `minWidth: '100%'`, which forces the table to
            the container's width and leaves the browser to hand the surplus out — and with
            `table-layout: fixed` it hands it to EVERY column proportionally. So dragging one
            column narrower widened all the others, which reads as the drag fighting back and
            is the one thing reference §4 says a resize must never do.

            `minWidth: 'max-content'` lets a genuinely wide table exceed its container and
            scroll while keeping every declared width exact.

            <b>THE FLEXING TABLE NEEDS IT TOO — 29 Sep 2026, and this is a fix rather than a
            tidy-up.</b> This branch read `{ tableLayout: 'fixed' }` alone, on the reasoning
            that a flexing table is pinned to its container and the two could not both be true.
            They can, and leaving the floor off is what broke resizing on the one table that
            uses this branch:

            With no minimum, the table is pinned to the container. The flex column asks for
            100% of it, so when the DECLARED widths already sum past the container the browser
            has nowhere to take that 100% from but the other columns — every one of them
            renders narrower than it was declared, which is the register grid showing "Selec"
            and "Serial n" in columns declared at 200px.

            And then a DRAG appears to do nothing: the dragged column gets its new pixel width,
            the 100% column re-claims the table, and everything is squeezed back to where it
            was. That is the drag fighting back — the exact failure the paragraph above says a
            resize must never do, reintroduced one branch along.

            With the floor the table can never be narrower than the sum of its columns, so the
            flex column always takes a genuine REMAINDER and a dragged width survives. A table
            wider than its container scrolls, which is what the other branch already does.
          */
          style={{ tableLayout: 'fixed', width: '100%', minWidth: `${declaredTotal}px` }}
        >
          <colgroup>
            {selectable ? <col style={{ width: GUTTER }} /> : null}
            <col style={{ width: numberGutter }} />
            {headers.map((header, index) => (
              /*
                THE FLEX COLUMN ASKS FOR 100%, it does not omit its width. An auto column in a
                fixed-layout table can be sized from its header cell's content rather than from
                the remainder, which strands the surplus in the trailing gutter — the blank band
                after the delete icon. Demanding 100% makes the fixed columns keep their widths
                and hands this one everything left, deterministically.
              */
              <col
                key={typeof header === 'string' ? header : header.label}
                style={
                  index === absorber
                    ? {
                        /*
                          EVERYTHING ELSE, as a literal — so this is a subtraction rather than a
                          circular reference. `100%` is the table's own width, which is the
                          container or the floor, whichever is larger.
                        */
                        width: `calc(100% - ${
                          declaredTotal - (effective[index] ?? colWidths[index])
                        }px)`,
                      }
                    : { width: effective[index] ?? colWidths[index] }
                }
              />
            ))}
            <col style={{ width: GUTTER }} />
          </colgroup>

          {/* Header band matches the medicine-table format exactly. */}
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {selectable ? (
                <th scope="col" className="sticky left-0 z-30 bg-slate-100 px-3 py-2">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={onToggleAll}
                    disabled={selectDisabled}
                    aria-label="Select all rows"
                  />
                </th>
              ) : null}

              <th
                scope="col"
                className="sticky z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 text-left first:border-l-0"
                style={{ left: selectable ? GUTTER : 0 }}
              >
                #
              </th>

              {headers.map((header, index) => {
                const frozen = index < freezeLeadingColumns;
                const label = typeof header === 'string' ? header : header.label;
                const align = typeof header === 'string' ? 'left' : (header.align ?? 'left');

                return (
                  <th
                    key={label}
                    scope="col"
                    className={[
                      'relative border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0',
                      align === 'right' ? 'text-right' : 'text-left',
                      frozen ? 'sticky z-30' : '',
                    ].join(' ')}
                    style={frozen ? { left: leadingWidth } : undefined}
                  >
                    {label}
                    {resizeKey ? (
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${label}`}
                        onPointerDown={(e) => startResize(index, e)}
                        className="absolute -right-0.5 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-blue-300"
                      />
                    ) : null}
                  </th>
                );
              })}

              {/* Remove-button gutter. */}
              <th scope="col" className="border-l border-slate-200 bg-slate-100 px-2 py-2">
                <span className="sr-only">Remove</span>
              </th>
            </tr>
          </thead>

          <tbody>{children}</tbody>

          <tfoot className="sticky bottom-0 z-20">
            <tr className="border-t border-slate-200 bg-white">
              {selectable ? <td className="sticky left-0 z-30 bg-white px-2 py-1.5" /> : null}

              {/*
                The Add affordance lives IN the leading gutter as a square icon-only button.
                It is never a labelled pill above the header.
              */}
              <td
                className="sticky z-30 border-l border-slate-200 bg-white px-1.5 py-1.5 first:border-l-0"
                style={{ left: selectable ? GUTTER : 0 }}
              >
                <div
                  className={
                    addMenu
                      ? 'inline-flex overflow-hidden rounded-md bg-brand-50 text-brand-800'
                      : 'contents'
                  }
                >
                  <Tooltip
                    label="Add row"
                    disabledReason={addDisabled ? addDisabledReason : null}
                  >
                    <button
                      type="button"
                      id={addButtonId}
                      onClick={onAddRow}
                      disabled={addDisabled}
                      aria-label="Add row"
                      className={`flex h-9 w-9 items-center justify-center text-brand-800 transition hover:bg-brand-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-300 ${
                        addMenu ? '' : 'rounded-md bg-brand-50'
                      }`}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </Tooltip>

                  {/*
                    THE CHEVRON IS ICON-ONLY, so it owes its name TWICE — the `aria-label` and the
                    `<Tooltip>`, which is `<ShortcutButton>`'s own rule. It is deliberately NOT
                    disabled with the `+`: filling the table from somewhere else is a different
                    act from adding one more blank row — it is disabled on its OWN entries, when
                    every one of them is dead.
                  */}
                  {addMenu ? (
                    <Menu
                      id={`${addButtonId}-menu-panel`}
                      items={addMenu}
                      align="left"
                      trigger={({ toggle }) => (
                        <Tooltip
                          label="More ways to add"
                          disabledReason={menuDead ? menuDeadReason : null}
                        >
                          <button
                            type="button"
                            id={`${addButtonId}-menu`}
                            onClick={toggle}
                            disabled={menuDead}
                            aria-label="More ways to add"
                            className="flex h-9 w-6 items-center justify-center border-l border-brand-200 text-brand-800 transition hover:bg-brand-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-300"
                          >
                            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </Tooltip>
                      )}
                    />
                  ) : null}
                </div>
              </td>

              <td
                colSpan={headers.length + 1}
                className="border-l border-slate-200 bg-white px-3 py-1.5"
              >
                {footerContent}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/**
 * A row inside `<InlineAddTable>`. The chrome owns both gutters; callers pass content cells
 * only.
 */
export function InlineRow({
  idx,
  onRemove,
  selected = false,
  onToggleSelect,
  selectable = false,
  className = '',
  drag,
  menu,
  children,
}: {
  /** The displayed row number. */
  idx: number;
  onRemove: () => void;
  /**
   * Opt-in row MENU — a kebab in the trailing gutter, IN PLACE of the trash button, for a grid
   * whose rows have more than one thing to do (an Edit beside the Delete). The caller puts its
   * own remove entry in `items`; `onRemove` is then unused by the chrome. Same `<Menu>` every
   * other row menu opens, so its gating and disabled reasons carry over.
   */
  menu?: { id: string; items: MenuItem[] };
  selected?: boolean;
  onToggleSelect?: () => void;
  selectable?: boolean;
  /** Data-state row tint, e.g. red for an infectious diagnosis row. */
  className?: string;
  /**
   * Opt-in drag handle. Only for grids where row ORDER is itself data — leave it off for
   * unordered grids, where a drag affordance implies a meaning that is not there.
   */
  drag?: {
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  children: ReactNode;
}) {
  return (
    <tr
      className={`group border-t border-slate-200 hover:bg-slate-50 ${className}`}
      draggable={Boolean(drag)}
      onDragStart={drag?.onDragStart}
      onDragOver={drag?.onDragOver}
      onDrop={drag?.onDrop}
    >
      {selectable ? (
        <td className="sticky left-0 z-10 bg-white px-3 py-1.5 group-hover:bg-slate-50">
          <Checkbox
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Select row ${idx}`}
          />
        </td>
      ) : null}

      <td
        className="sticky z-10 border-l border-slate-200 bg-white px-3 py-1.5 font-mono tabular-nums text-xs text-slate-400 first:border-l-0 group-hover:bg-slate-50"
        style={{ left: selectable ? GUTTER : 0 }}
      >
        {drag ? (
          <span className="flex cursor-grab items-center" aria-hidden="true">
            <GripVertical className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400" />
          </span>
        ) : (
          idx
        )}
      </td>

      {children}

      <td className="border-l border-slate-200 px-2 py-1.5 text-right">
        {menu ? (
          <Menu
            id={menu.id}
            items={menu.items}
            align="right"
            trigger={({ open, toggle }) => (
              <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Row ${idx} actions`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
              >
                <MoreVertical className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          />
        ) : (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove row ${idx}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </td>
    </tr>
  );
}
